import { useDrugMaster } from '@hc/store/backoffice/drugMaster';
import { EditIcon, Switch } from '@hc/ui/atoms';
import { MasterTable, MasterTopContainer } from '@hc/ui/components/backoffice';
import { useAllergies } from '@hc/store/backoffice/allergies';
import { AllergiesDrawer, TableSkeletonLoader } from '@hc/ui/components';
import { getDateFormatToString } from '@hc/dayjs';
import { Pagination } from '@hc/ui/components/backoffice/pagination';
import { queryClient } from '@hc/utils';
import { Box, Grid, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import isEqual from 'react-fast-compare';
import { AllergiesMaster_Style } from './style';

export default function AllergiesMaster(props) {
  const { ...rest } = props;

  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Store Data
  const {
    allergiesData,
    allergiesCount,
    getAllAllergies,
    allergiesStatusUpdate,
    addEditAllergyMaster,
    isIamValidToSave,
    initialEditAllergies,
    clearAllergiesData,
    loading,
  } = useAllergies((state) => ({
    allergiesData: state.allergiesMasterData,
    allergiesCount: state.allergiesMasterCount,
    getAllAllergies: state.getAllAllergies,
    allergiesStatusUpdate: state.allergiesStatusUpdate,
    addEditAllergyMaster: state.addEditAllergyMaster,
    isIamValidToSave: state.isIamValidToSave,
    initialEditAllergies: state.initialEditAllergies,
    clearAllergiesData: state.clearAllergiesData,
    loading: state.loading,
  }));

  //  ----- TABLE DATA SEARCH ---------
  const handleSearch = async (searchValue) => {
    setSearch(searchValue);
    await getAllAllergies(searchValue, rowsPerPage);
    setOffset(0);
  };

  // ------TABLE DATA SWITCH --------
  const onStatusChange = async (val) => {
    const payload = {
      id: val.id ?? 0,
      active_status: !val?.is_active ?? true,
    };
    const response = await allergiesStatusUpdate(payload);
    if (response === 200) {
      queryClient.invalidateQueries({
        queryKey: ['getAllAllergies', search, offset],
      });
      await getAllAllergies(search, rowsPerPage, offset);
    }
  };

  // ------PAGINATION ------------
  const onLimitChange = async (limit, offset) => {
    setRowsPerPage(limit);
    setOffset(offset);
    queryClient.invalidateQueries({
      queryKey: ['getAllAllergies', search, offset],
    });
    await getAllAllergies(search, limit, offset);
  };

  const onOffsetchange = async (offset) => {
    setOffset(offset);
    await getAllAllergies(search, rowsPerPage, offset);
  };

  // ADD
  const upsertMaster = async () => {
    const result = isIamValidToSave();
    if (result) {
      const response = await addEditAllergyMaster();
      if (response?.status?.code === 200) {
        setDrawerOpen(false);
        clearAllergiesData();
        queryClient.invalidateQueries({
          queryKey: ['getAllAllergies', search, offset],
        });
        await getAllAllergies(search, rowsPerPage, offset);
      }
    }
  };

  const handleAddClick = () => {
    setDrawerOpen(true);
  };
  const closeDrugMasterDrawer = () => {
    setDrawerOpen(false);
    clearAllergiesData();
  };
  const onEdit = (val) => {
    initialEditAllergies(val);
    setDrawerOpen(true);
  };

  //  TABLE COLUMNS META DATA
  const columns = [
    {
      field: 'id',
      headerName: 'Id',
      width: 60,
    },
    {
      field: 'allergy',
      headerName: 'Allergy',
      width: 180,
    },
    {
      field: 'status',
      headerName: 'Active/Inactive',
      width: 150,
      renderCell: (params) => (
        <Switch
          id="switch"
          checked={params?.row?.status}
          onChange={() => onStatusChange(params?.row?.value)}
          rootStyle={AllergiesMaster_Style.switchSx}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 200,
      valueGetter: (params) =>
        getDateFormatToString(params.row.created_at, 'lll'),
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      width: 200,
      valueGetter: (params) =>
        getDateFormatToString(params.row.updated_at, 'lll'),
    },
    {
      field: 'edit',
      headerName: 'Edit',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ pl: 1 }} onClick={() => onEdit(params?.row?.value)}>
          <EditIcon id ='editAllegry' style={{ cursor: 'pointer', color: '#007965' }} />
        </Box>
      ),
    },
  ];

  //  INITIAL DATA FETCH

  const initialData = async () => {
    await getAllAllergies('');
  };

  useEffect(() => {
    initialData();
  }, []);

  return (
    <Box
      {...rest}
      p={2}
      sx={{
        height: '100vh',
        overflowY: 'scroll',
        backgroundColor: '#fff',
        borderRadius: '10px',
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <MasterTopContainer
            isMasterTitleShow
            isTitileShow={false}
            handleSearch={handleSearch}
            handleAddClick={handleAddClick}
            title="Allergies"
            count={allergiesCount}
            showSearchField
            searchFieldPlaceholder="Search allergies by name"
            showAddButton
            addButtonName="Add Allergies"
            addMasterStyle={{
              boxShadow:
                '0px 3px 1px -2px rgba(0,0,0,0.2),0px 3px 3px 0px rgba(0,0,0,0.14),0px 1px 2px 0px rgba(0,0,0,0.12)',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          {loading ? (
            <TableSkeletonLoader />
          ) : (
            <Box>
              <MasterTable
                columns={columns}
                data={allergiesData ?? []}
                customTableStyle={AllergiesMaster_Style.dataTableCustomSx}
                hideFooter
                rootStyle={{ height: '68vh' }}
              />
            </Box>
          )}
          <Stack
            flexDirection="row"
            justifyContent="end"
            sx={{ width: '100%' }}
          >
            <Pagination
              totalCount={allergiesCount}
              onLimitChange={onLimitChange}
              onOffsetchange={onOffsetchange}
              offset={offset}
              rowsPerPage={rowsPerPage}
            />
          </Stack>
        </Grid>
      </Grid>
      <AllergiesDrawer
        drawerOpen={drawerOpen}
        closeMasterDrawer={closeDrugMasterDrawer}
        upsertMaster={upsertMaster}
      />
    </Box>
  );
}

AllergiesMaster.propTypes = {
  className: PropTypes.string,
};
